import { Component, Prop, Event, EventEmitter, Method, State, Element, h } from '@stencil/core';
import { Color, Size } from '../../interface';
import isNumber from 'lodash-es/isNumber';
import isArray from 'lodash-es/isArray';
import { RadioData } from './radio-button-interface';

@Component({
  tag: 'lar-radio-button',
  styleUrl: 'radio-button.scss',
  shadow: true
})
export class RadioButton {
  @Element() el!: HTMLLarDebugElement;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'device';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() loading: boolean = true;
  @State() selected?: number;
  @State() radios?: RadioData[];

  private node!: HTMLLarNodeElement;

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.output.emit(); // get selected
    this.output.emit({ command: 'get_radio_list' });
  }
  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }
  /**
   * Input Larva event message (see abow)
   */
  @Method()
  async input(data: any) {
    if (isNumber(data)) {
      this.selected = data;
    } else if (isArray(data)) {
      this.radios = data;
    }
    if (this.radios !== undefined && this.selected !== undefined) {
      this.loading = false;
    }
  }

  private selectOption(index: number) {
    this.loading = true;
    this.output.emit(index);
  }

  render() {
    const value = (this.radios !== undefined && this.selected !== undefined) ? this.radios[this.selected].name : '';
    const componentProps = {
      onSelectoption: (e: CustomEvent) => this.selectOption(e.detail),
      disabled: this.loading,
      color: this.colorInputs || this.color,
      radios: this.radios,
      selected: this.selected
    };
    return [
      <lar-node
        icon={this.icon}
        hideTitles={this.hideTitles}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        colorIconSmall={this.colorIconSmall}
        value={value}
        log={this.log}
        loading={this.loading}
        nodeSize={this.nodeSize}
        component="lar-radio-button-content"
        componentProps={componentProps}
      >
        <slot />
      </lar-node>
    ];
  }
}
